import { useEffect, useState } from "react";
import styles from "./MarketPlaceDetail.module.css";
import ProductOwnership from "../../MarketPlace/MarketPlaceDetail/ProductOwnership";
import { CircularProgress, MenuItem, Select } from "@mui/material";
import MarketPlaceT1 from "./MarketPlaceT1";
import MarketPlaceT2 from "./MarketPlaceT2";
import MarketPlaceT3 from "./MarketPlaceT3";
import DynamicPlanInterval from "./DynamicPlanInterval";
import MarketPlaceRequiredField from "../MarketPlacePricing/MarketPlaceRequiredField";
import { BsArrowLeftCircle } from "react-icons/bs";
import { BiSolidEdit } from "react-icons/bi";
import { RxCross2 } from "react-icons/rx";
import { Button } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { pinnedSkipMessage } from "../../../utils/stripeSyncPinned";
import SubmitConfirmation from "./SubmitConfirmation";
// Phase 4 — "who is this price for?" and the pending list on a plan.
import ScheduledPriceChanges from "../../Catalog/ScheduledPriceChanges";
import SortableList, { DragHandle } from "./SortableList";
import ListingEditorSidebar from "./ListingEditorSidebar";
import MarketplacePreview from "./MarketplacePreview";
import FieldInfo from "./FieldInfo";
import { FIELD_INFO } from "./fieldInfoContent";
import FieldGuide from "./FieldGuide";
import { isDefaultRow } from "../../../utils/unitPrice";

// FIX 1 — OUTER-SAVE UNIT-PRICE GATE. Inline unit-price edits commit live and
// are intentionally NOT validated per-keystroke (so typing a half-finished
// range/price isn't blocked). That left the outer Save with no unit-field
// validation at all — it only checked unit_ids was a non-empty array — so blank
// prices, inverted/overlapping ranges, zero-or-multiple Defaults and bad Steps
// could persist to live billing. This ports the per-tier rules that live in the
// inline editors' `validateUnitPriceForms` and runs them across EVERY
// non-"contact" plan before the POST. Returns a human-readable error string
// (naming the plan + tier) on the first failure, or null when all plans pass.
const isBlankCell = (v) => v === "" || v === null || v === undefined;

const validateUnitPricesForSave = (plansList, planTypeList) => {
  for (const plan of plansList) {
    if (plan.price_type === "contact") continue;
    const units = Array.isArray(plan.unit_ids) ? plan.unit_ids : [];
    // Empty unit_ids on an Active plan is already blocked by validatePlans;
    // there are simply no tier fields to validate here.
    if (units.length === 0) continue;
    const template = planTypeList[plan.typeId]?.template || plan.plan_type;
    const label = plan.name?.trim() ? plan.name.trim() : "Unnamed plan";

    // Only HARD-BLOCK on things that would break live billing if wrong:
    // a missing/NaN marketplace price (what the customer is charged) and
    // invalid/overlapping ranges (which tier bills). Everything else
    // (industry/vendor price, HSN, Steps, Default count) is NOT required —
    // legacy plans routinely lack them, and blocking would trap the admin from
    // editing existing listings. Optional numeric fields are only rejected when
    // PRESENT-and-non-numeric, never when blank.
    const numericIfPresent = (u, f, i) => {
      if (isBlankCell(u[f])) return null;
      if (!Number.isFinite(Number(u[f]))) {
        return `${label} · tier ${i + 1}: ${f.replace(/_/g, " ")} must be a valid number.`;
      }
      return null;
    };

    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      // Marketplace price is billing-critical → required + numeric.
      if (isBlankCell(u.Marketplace_price)) {
        return `${label} · tier ${i + 1}: Marketplace price is required.`;
      }
      if (!Number.isFinite(Number(u.Marketplace_price))) {
        return `${label} · tier ${i + 1}: Marketplace price must be a valid number.`;
      }
      // Ranges define which tier bills → required + valid.
      if (isBlankCell(u.StartRange) || isBlankCell(u.EndRange)) {
        return `${label} · tier ${i + 1}: start range and end range are required.`;
      }
      const s = Number(u.StartRange);
      const e = Number(u.EndRange);
      if (!(Number.isFinite(s) && Number.isFinite(e)) || e <= s) {
        return `${label} · tier ${i + 1}: End Range must be greater than Start Range.`;
      }
      // Optional fields: reject only if present and non-numeric.
      for (const f of ["Industry_price", "Vendor_price", "Min_price", "Max_price"]) {
        const err = numericIfPresent(u, f, i);
        if (err) return err;
      }
      // Steps (credit) only validated when present.
      if (template === "credit" && !isBlankCell(u.Steps)) {
        const steps = Number(u.Steps);
        if (!Number.isInteger(steps) || steps < 1 || steps > e - s) {
          return `${label} · tier ${i + 1}: Steps must be a whole number between 1 and ${e - s}.`;
        }
      }
    }
    // Tiers must not overlap (wrong tier would bill). Applies to all templates.
    const ranges = units
      .map((u, i) => ({ i, s: Number(u.StartRange), e: Number(u.EndRange) }))
      .filter((r) => Number.isFinite(r.s) && Number.isFinite(r.e) && r.e > r.s)
      .sort((a, b) => a.s - b.s);
    for (let k = 1; k < ranges.length; k++) {
      if (ranges[k].s <= ranges[k - 1].e) {
        return `${label}: unit tiers must not overlap.`;
      }
    }
  }
  return null;
};

function MarketPlaceDetails({ selectedMarketPlace, setSelectedMarketPlace }) {
  const [paymentStatus, setPaymentStatus] = useState("");
  const [activeStatus, setActiveStatus] = useState("Active");
  const [showPlanType, setshowPlanType] = useState(false);
  const [typeInput, setTypeInput] = useState({});
  const [planType, setPlanType] = useState([
    {
      skuId: "",
      name: "Default",
      parentId: "",
      levels: "",
      template: "recurring",
      required_fields: [],
      allow_delete: true,
    },
  ]);
  const [editingPlanIndex, setEditingPlanIndex] = useState(null);
  const [selected, setSelected] = useState(0);
  const [interval, setInterval] = useState({});
  const [showInterval, setShowInterval] = useState(false);
  const [intervals, setIntervals] = useState([]);
  const [selectInterval, setSelectedInterval] = useState(0);
  const [editingIntervalIndex, setEditingIntervalIndex] = useState(null);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [isSomethingChanged, setIsSomethingChanged] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [planErrors, setPlanErrors] = useState({});
  const [featureErrors, setFeatureErrors] = useState({});

  // Show Required Model state
  const [showRequiredFieldModal, setShowRequiredFieldModal] = useState(false);
  const [submitconfirmation, setSubmitConfirmation] = useState(false);
  // True when the pending save changes unit prices on a plan that is locked
  // (allow_delete === false) because it has active subscriptions. Drives an
  // extra hard warning in the submit confirmation dialog.
  const [activeSubPriceChange, setActiveSubPriceChange] = useState(false);
  const [productVendorId, setProductVendorId] = useState(null);
  const adminVendorId = -1;

  // Right slide-in listing-editor (presentation shell only).
  const [editorOpen, setEditorOpen] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    try {
      async function fetchData() {
        setFetchLoader(true);
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/get_marketplace_pricing`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ skuId: selectedMarketPlace.id }),
          },
        );
        const data = await res.json();
        setFetchLoader(false);
        if (data.status === 200) {
          setPlanType(data.planType);
          const intervalsWithIndex = data.intervals.map((int, index) => ({
            ...int,
            index,
          }));

          setIntervals(intervalsWithIndex);
          setPlans(
            data.plans.map((p) => {
              const intv = intervalsWithIndex[p.interval] || {};

              return {
                ...p,
                n_price_validity_interval:
                  p.n_price_validity_interval ?? intv.n_interval ?? "",
                price_validity_interval:
                  p.price_validity_interval ?? intv.interval ?? "",
              };
            }),
          );
          setFeatures(data.features);
          if (!data.planType.length) {
            setPlanType([
              {
                skuId: "",
                name: "Default",
                parentId: "",
                levels: "",
                template: "recurring",
                required_fields: [],
                allow_delete: true,
              },
            ]);
          }
          setSelectedInterval(0);
          setProductVendorId(data.product_vendor_id);
        } else {
          toast.error("Something went wrong", "error");
        }
      }
      fetchData();
    } catch (error) {
      console.log("Error", error);
    }
    // eslint-disable-next-line
  }, [isSomethingChanged]);

  useEffect(() => {
    const first = intervals.find((i) => i.typeId === selected);
    setSelectedInterval(first ? first.index : null);
    // eslint-disable-next-line
  }, [selected]);

  const handleSaveType = (obj) => {
    if (!obj) {
      return;
    }
    if (obj) {
      if (editingPlanIndex !== null) {
        const temp = [...planType];
        temp[editingPlanIndex].name = obj.name;
        temp[editingPlanIndex].template = obj.template;
        setPlanType(temp);
        setSelected(editingPlanIndex);
      } else {
        setPlanType((prev) => [...prev, obj]);
        setSelected(planType.length);
      }
      setshowPlanType(false);
      setTypeInput({});
      setEditingPlanIndex(null);
    }
  };

  const handleEditType = (type, index) => {
    setshowPlanType(true);
    setTypeInput(type);
    setEditingPlanIndex(index);
  };

  const handleDeleteType = (index) => {
    if (planType[index].vendor_id !== adminVendorId) {
      toast.error("Can't Delete Type created by other vendors");
      return;
    }
    if (planType.length <= 1) {
      toast.error("Can't Delete Type, atleast one type is mandatory");
      return;
    }
    const isTypeUsed = intervals.some((interval) => interval.typeId === index);
    if (isTypeUsed) {
      toast.error(
        "Can't Delete Type, Delete all child intervals to delete this type",
      );
      return;
    }

    // typeId is an array index into planType. Removing one shifts every later
    // type down by one, so every child pointer (intervals/plans/features) that
    // referenced a type after `index` must be remapped — otherwise they silently
    // re-attach to the wrong type. (Mirror of the reorder remap logic.)
    const remapTypeId = (tid) => {
      const n = Number(tid);
      return n > index ? n - 1 : n;
    };
    setPlanType((prev) => prev.filter((_, i) => i !== index));
    setIntervals((prev) =>
      prev
        .filter((iv) => Number(iv.typeId) !== index)
        .map((iv) => ({ ...iv, typeId: remapTypeId(iv.typeId) })),
    );
    setPlans((prev) =>
      prev
        .filter((p) => Number(p.typeId) !== index)
        .map((p) => ({ ...p, typeId: remapTypeId(p.typeId) })),
    );
    setFeatures((prev) =>
      prev
        .filter((f) => Number(f.typeId) !== index)
        .map((f) => ({ ...f, typeId: remapTypeId(f.typeId) })),
    );
    setSelected((prevSel) => {
      const adjusted = prevSel > index ? prevSel - 1 : prevSel;
      return Math.max(0, Math.min(adjusted, planType.length - 2));
    });
  };

  const handleSaveInterval = (data) => {
    if (!data) {
      return;
    }
    if (data) {
      if (editingIntervalIndex !== null) {
        let temp = intervals;
        data["index"] = editingIntervalIndex;
        temp[editingIntervalIndex] = data;
        setIntervals(temp);
        setSelectedInterval(editingIntervalIndex);
      } else {
        setIntervals((prev) => {
          data["index"] = prev.length;
          setSelectedInterval(prev.length);
          return [...prev, data];
        });
      }
      setShowInterval(false);
      setInterval({});
      setEditingIntervalIndex(null);
    }
  };

  const handleEditInterval = (interval, index) => {
    if (intervals[index].vendor_id !== adminVendorId) {
      toast.error("Can't Edit Interval created by other vendors");
      return;
    }
    if (interval.allow_delete) {
      setShowInterval(true);
      setInterval(interval);
      setEditingIntervalIndex(index);
    } else {
      toast.error(
        "Can't Edit Interval, This has a prices with active subscriptions",
      );
    }
  };

  const handleDeleteInterval = (index) => {
    if (intervals[index].vendor_id !== adminVendorId) {
      toast.error("Can't Delete Interval created by other vendors");
      return;
    }
    const isIntervalUsed = plans.some((plan) => Number(plan.interval) === index);
    if (isIntervalUsed) {
      toast.error(
        "Can't Delete Interval, Delete all child plans to delete this interval",
      );
      return;
    }
    // plans reference an interval by its array index; removing one shifts every
    // later interval down, so remap plans[].interval and rewrite each surviving
    // interval's own `index` field to its new position.
    const nextIntervals = intervals
      .filter((_, i) => i !== index)
      .map((iv, i) => ({ ...iv, index: i }));
    setIntervals(nextIntervals);
    setPlans((prev) =>
      prev
        .filter((p) => Number(p.interval) !== index)
        .map((p) => ({
          ...p,
          interval: Number(p.interval) > index ? Number(p.interval) - 1 : Number(p.interval),
        })),
    );
    let lastMatch = null;
    for (let i = nextIntervals.length - 1; i >= 0; i--) {
      if (nextIntervals[i].typeId === selected) {
        lastMatch = i;
        break;
      }
    }
    setSelectedInterval(lastMatch);
  };

  //REQUIRED FIELD MODAL HANDLERS:
  const requiredFieldOpenHandler = () => {
    setShowRequiredFieldModal(true);
  };

  const requiredFieldCloseHandler = () => {
    setShowRequiredFieldModal(false);
  };

  const filteredIntervalData = intervals.filter((i) => i.typeId === selected);
  const filteredFeaturesData = features.filter((f) => f.typeId === selected);

  const validatePlans = (allPlans) => {
    let errors = {};
    let isValid = true;
    const plans = allPlans;
    if (plans.length === 0) {
      return false;
    }
    for (const plan of plans) {
      const planError = {};
      if (typeof plan.name !== "string" || plan.name.trim() === "") {
        planError.name = true;
        isValid = false;
      }
      if (
        typeof plan.description !== "string" ||
        plan.description.trim() === ""
      ) {
        planError.description = true;
        isValid = false;
      }

      // A plan another vendor sells (plan.held_by) needs no house price:
      // leaving it empty just means Easexpense does not sell it too.
      const heldEmpty =
        Boolean(plan.held_by) &&
        (!Array.isArray(plan.unit_ids) || plan.unit_ids.length === 0);
      if (
        !heldEmpty &&
        ((plan.price_type !== "contact" &&
          plan.status === "Active" &&
          !Array.isArray(plan.unit_ids)) ||
          (plan.price_type !== "contact" &&
            plan.status === "Active" &&
            plan.unit_ids.length === 0))
      ) {
        planError.price = true;
        isValid = false;
      }

      if (!plan.region) {
        planError.region = true;
        isValid = false;
      }

      if (!plan.modify_orders) {
        planError.modify_orders = true;
        isValid = false;
      }

      if (!plan.unit_price_type) {
        planError.unit_price_type = true;
        isValid = false;
      }

      if (Object.keys(planError).length > 0) {
        errors[plan.id] = planError;
        isValid = false;
      }
    }
    setPlanErrors(errors);
    return isValid;
  };

  // A feature comparison row is identified by `key` + `typeId`: the heading
  // lives in `features` (keyed by both), while each plan carries the per-plan
  // values in `plan.features` (joined by `key`, scoped to the plan's typeId).
  // Legacy `duplicatePlan` corruption left two kinds of invisible dead rows the
  // admin can't see or edit on any tab, yet which now fail the "all types"
  // feature validation and block the save:
  //   1. A heading that is ENTIRELY empty — blank name AND blank titles AND
  //      blank across every plan value of its type — i.e. it carries no info.
  //   2. An orphaned per-plan value entry whose `key` has no matching heading
  //      for that plan's own type (cross-type pollution).
  // Only these are auto-cleaned before validating/sending. A row that carries
  // SOME content but is missing a required cell is a genuine error and is left
  // in place so validation still blocks it.
  const isEmptyCell = (v) => !v || (typeof v === "string" && v.trim() === "");

  const cleanLegacyFeatures = (featuresList, plansList) => {
    // Identify entirely-empty headings (scoped by key+typeId) to drop.
    const droppedHeadings = new Set();
    featuresList.forEach((feature) => {
      const headingBlank = isEmptyCell(feature?.name);
      const titlesBlank = (feature?.data || []).every(isEmptyCell);
      const valuesBlank = plansList
        .filter((p) => Number(p.typeId) === Number(feature.typeId))
        .every((p) =>
          (p.features || [])
            .filter((f) => f.key === feature.key)
            .every((f) => (f.data || []).every(isEmptyCell)),
        );
      if (headingBlank && titlesBlank && valuesBlank) {
        droppedHeadings.add(`${feature.key}::${feature.typeId}`);
      }
    });

    const cleanedFeatures = featuresList.filter(
      (f) => !droppedHeadings.has(`${f.key}::${f.typeId}`),
    );

    // Surviving heading keys per typeId — any plan value whose key isn't here
    // for the plan's own type is orphaned (its heading was dropped, or it's
    // cross-type pollution) and gets stripped. Scoping by key+typeId ensures a
    // legitimately in-progress feature (which always has a same-type heading)
    // is never removed.
    const keysByType = new Map();
    cleanedFeatures.forEach((f) => {
      const t = Number(f.typeId);
      if (!keysByType.has(t)) keysByType.set(t, new Set());
      keysByType.get(t).add(f.key);
    });

    const cleanedPlans = plansList.map((p) => {
      const validKeys = keysByType.get(Number(p.typeId)) || new Set();
      return {
        ...p,
        features: (p.features || []).filter((f) => validKeys.has(f.key)),
      };
    });

    return { cleanedFeatures, cleanedPlans };
  };

  const validateFeaturs = (allFeatures, allPlans = plans) => {
    let error = {};
    let isValid = true;
    allFeatures.forEach((feature) => {
      const featureErr = {
        heading: false,
        titles: [],
        values: [],
      };
      // Heading must be present...
      if (!feature?.name || feature.name.trim() === "") {
        featureErr.heading = true;
        isValid = false;
      }
      // ...AND the row titles + per-plan values are validated unconditionally
      // (previously these checks were nested inside the heading-empty guard, so
      // a named heading skipped all of them — empty cells reached customers).
      (feature.data || []).forEach((title, idx) => {
        if (!title || title.trim() === "") {
          featureErr.titles[idx] = true;
          isValid = false;
        } else {
          featureErr.titles[idx] = false;
        }
      });
      allPlans.forEach((plan) => {
        (plan.features || [])
          .filter((f) => f.key === feature.key)
          .forEach((f) => {
            (f.data || []).forEach((val, idx) => {
              if (!val || (typeof val === "string" && val.trim() === "")) {
                featureErr.values[idx] = true;
                isValid = false;
              } else {
                featureErr.values[idx] = featureErr.values[idx] ?? false;
              }
            });
          });
      });
      if (
        featureErr.heading ||
        featureErr.titles.includes(true) ||
        featureErr.values.includes(true)
      ) {
        error[feature.key] = featureErr;
      }
    });
    setFeatureErrors(error);
    return isValid;
  };

  const handleSubmit = async () => {
    try {
      // Strip invisible legacy dead rows (entirely-empty headings + orphaned /
      // cross-type value entries) so residue from the old duplicatePlan
      // corruption can't block the save. Commit the cleaned data to state so
      // handleSave sends the cleaned payload too.
      const { cleanedFeatures, cleanedPlans } = cleanLegacyFeatures(
        features,
        plans,
      );
      setFeatures(cleanedFeatures);
      setPlans(cleanedPlans);

      const isValidPlan = validatePlans(cleanedPlans);
      // Validate features of ALL types (not just the selected one) — plans of
      // every type are saved together, so a hidden type with empty feature
      // cells must also block the save.
      const isValidFeature = validateFeaturs(cleanedFeatures, cleanedPlans);
      if (!isValidPlan) {
        toast.error("Plan fields are required, check all types & intervals!");
        return;
      }
      if (!isValidFeature) {
        toast.error(
          "Feature fields are required, check all types & intervals!",
        );
        return;
      }

      // FIX 1: the critical data-integrity gate. Because inline unit-price edits
      // commit live WITHOUT per-keystroke validation, this is the only place
      // that validates the actual tier fields (prices, ranges, steps, Default)
      // before they reach live billing. Block the save — no confirm, no POST —
      // and surface the offending plan + tier if anything is invalid.
      const unitPriceError = validateUnitPricesForSave(cleanedPlans, planType);
      if (unitPriceError) {
        toast.error(unitPriceError);
        return;
      }

      // Price validity is editable on a live plan now, so it needs the one
      // rule that actually matters: a price cannot be guaranteed for less
      // time than the customer is billed for, or the promise expires
      // mid-cycle and the lock has no way to express that.
      const DURATION_DAYS = { day: 1, week: 7, month: 30, quarter: 91, year: 365 };
      const spanDays = (n, unit) => {
        const u = String(unit || "").toLowerCase();
        if (u === "lifetime") return Infinity;
        const per = DURATION_DAYS[u];
        const count = Number(n);
        if (!per || !Number.isFinite(count) || count <= 0) return null;
        return per * count;
      };
      const shortValidity = cleanedPlans.find((p) => {
        const v = spanDays(p.n_price_validity_interval, p.price_validity_interval);
        const b = spanDays(p.n_interval, p.interval);
        return v !== null && b !== null && v < b;
      });
      if (shortValidity) {
        toast.error(
          `"${shortValidity.name || "A plan"}": price validity cannot be shorter than the billing period (${shortValidity.n_interval || 1} ${shortValidity.interval}).`,
        );
        return;
      }

      // Hard confirm when prices changed on a plan locked by active
      // subscriptions. Because unit-price edits now commit live, this reads the
      // per-plan changed flag / any changed unit row directly.
      const lockedPriceChange = cleanedPlans.some(
        (p) =>
          p.allow_delete === false &&
          (p.changed_unit_ids === true ||
            (Array.isArray(p.unit_ids) &&
              p.unit_ids.some((u) => u.changed_unitprice))),
      );
      setActiveSubPriceChange(lockedPriceChange);

      setSubmitConfirmation(true);
    } catch (error) {
      console.log("Validation Error", error);
    }
  };

  const handleSave = async (rollout = null) => {
    try {
      setSubmitLoader(true);
      // Normalize plan.tags to an array before saving. The tags <input> binds an
      // array and writes back a raw comma string on edit, which would otherwise
      // be persisted to the JSON column as a string and break array consumers.
      const normalizedPlans = plans.map((p) => ({
        ...p,
        tags: Array.isArray(p.tags)
          ? p.tags
          : String(p.tags ?? "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
      }));
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/update_marketplace_pricing`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            plans: normalizedPlans,
            features,
            planType,
            intervals,
            skuId: selectedMarketPlace.id,
            // Phase 4: who the new price version is for. Omitted (server
            // default "new customers only") when nothing re-priced.
            ...(rollout ? { rollout } : {}),
          }),
        },
      );
      const data = await res.json();
      setSubmitLoader(false);
      if (data.status === 200) {
        setSubmitConfirmation(false);
        const sync = data.stripe_sync;
        if (sync && sync.ok === false) {
          const n =
            sync.failed ??
            (Array.isArray(sync.failures) ? sync.failures.length : null);
          toast.warn(
            `Prices saved but Stripe sync failed${
              n ? ` for ${n} subscription${n === 1 ? "" : "s"}` : ""
            } — check subscription price update queue`,
          );
        } else {
          toast.success("Saved Successfully");
        }
        const pinnedMsg = pinnedSkipMessage(sync);
        if (pinnedMsg) toast.warn(pinnedMsg, { autoClose: 9000 });
        setIsSomethingChanged(!isSomethingChanged);
      } else {
        toast.error("Failed to save, try again");
      }
    } catch (error) {
      console.log("Error", error);
      toast.error("Failed to save, try again");
    }
  };

  // Shared visibility predicate for the plan cards: the plans of the currently
  // selected type+interval, further narrowed by the status/region filters.
  // Extracted so the plan-card reorder can compute the SAME visible subset.
  const matchesPlanFilters = (p) =>
    p.typeId === selected &&
    p.interval === selectInterval &&
    (activeStatus === "" ||
      (activeStatus === "Active" && p.status === "Active") ||
      (activeStatus === "Inactive" && p.status === "Inactive") ||
      (activeStatus === "Out Of Stock" && p.status === "Out Of Stock")) &&
    (paymentStatus === "" ||
      (paymentStatus === "GL" && p.region === "GL") ||
      (paymentStatus === "US" && p.region === "US") ||
      (paymentStatus === "IN" && p.region === "IN"));

  const filteredAppsData = plans.filter(matchesPlanFilters);

  const isOwner = adminVendorId === productVendorId;
  const currentInterval =
    intervals.find((i) => i.index === selectInterval) || null;
  const currentTemplate = planType[selected]?.template;

  // Drag-reorder the visible plan cards. The customer-facing display order is
  // the plans[] array position (the backend derives each plan's sort_order from
  // it), so reordering the cards here is all that's needed — handleSave already
  // persists the new order. Mirror of the Interval slot-remap above: capture
  // each visible plan's index in the FULL plans array, reorder only the visible
  // subset, then write the reordered plans back into those same original slots,
  // so plans hidden by the current type/interval/status/region filters keep
  // their positions untouched.
  const renderSortablePlanCards = () => {
    const subset = plans
      .map((p, idx) => ({ ...p, originalIndex: idx }))
      .filter((p) => matchesPlanFilters(p));
    return (
      <SortableList
        items={subset}
        strategy="grid"
        disabled={!isOwner}
        getId={(p) =>
          p.id != null ? `plan-${p.id}` : `plan-orig-${p.originalIndex}`
        }
        onReorder={(nextSubset) => {
          const slotIndices = subset.map((s) => s.originalIndex);
          const next = [...plans];
          nextSubset.forEach((item, i) => {
            const slot = slotIndices[i];
            const { originalIndex, ...clean } = item;
            next[slot] = clean;
          });
          setPlans(next);
        }}
        renderItem={(p, i, { sortableProps, dragHandleProps }) => (
          <div
            ref={sortableProps.ref}
            style={sortableProps.style}
            key={p.id != null ? `plan-${p.id}` : `plan-orig-${p.originalIndex}`}
          >
            {isOwner && (
              <DragHandle
                {...dragHandleProps}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  background: "rgba(255,255,255,0.85)",
                  borderRadius: 4,
                }}
              />
            )}
            <MarketplacePreview
              plan={p}
              interval={currentInterval}
              template={currentTemplate}
            />
          </div>
        )}
      />
    );
  };

  const steps = [
    { n: 1, label: "Type" },
    { n: 2, label: "Interval" },
    { n: 3, label: "Price" },
  ];
  const stepUnlocked = (n) => {
    if (n === 1) return true;
    if (n === 2) return planType[selected] != null;
    return planType[selected] != null && selectInterval != null;
  };
  const canNext =
    step === 1 ? planType[selected] != null : selectInterval != null;

  const openEditor = (atStep = 1) => {
    setStep(atStep);
    setEditorOpen(true);
  };

  /* ---------- Step 1 — TYPE + required fields ---------- */
  const step1Content = (
    <div className={styles.stepSection}>
      <div className={styles.planTypecontainer}>
        <div className={styles.typeheading}>
          Plan type
          <FieldInfo text={FIELD_INFO["type.template"]} label="About plan type" />
        </div>
        <ul className={styles.plan_list}>
          <SortableList
            items={planType}
            strategy="horizontal"
            disabled={adminVendorId !== productVendorId}
            getId={(type, idx) => (type.id != null ? `t-${type.id}` : `t-new-${idx}`)}
            onReorder={(next) => {
              const currentSelected = planType[selected];
              const newSelectedIdx = currentSelected
                ? next.findIndex(
                    (t) =>
                      (currentSelected.id != null
                        ? t.id === currentSelected.id
                        : t === currentSelected),
                  )
                : selected;

              const indexMap = new Map();
              planType.forEach((t, oldIdx) => {
                const newIdx = next.findIndex(
                  (n) => (t.id != null ? n.id === t.id : n === t),
                );
                if (newIdx >= 0) indexMap.set(oldIdx, newIdx);
              });
              setPlanType(next);
              setIntervals((prev) =>
                prev.map((iv) => {
                  const remapped = indexMap.get(Number(iv.typeId));
                  return remapped === undefined
                    ? iv
                    : { ...iv, typeId: remapped };
                }),
              );
              setPlans((prev) =>
                prev.map((p) => {
                  const remapped = indexMap.get(Number(p.typeId));
                  return remapped === undefined
                    ? p
                    : { ...p, typeId: remapped };
                }),
              );
              setFeatures((prev) =>
                prev.map((f) => {
                  const remapped = indexMap.get(Number(f.typeId));
                  return remapped === undefined
                    ? f
                    : { ...f, typeId: remapped };
                }),
              );
              if (newSelectedIdx >= 0) setSelected(newSelectedIdx);
            }}
            renderItem={(type, i, { sortableProps, dragHandleProps }) => (
              <li
                ref={sortableProps.ref}
                style={sortableProps.style}
                onClick={() => {
                  setSelected(i);
                }}
                key={type.id != null ? `t-${type.id}` : `t-new-${i}`}
                className={`${styles.plan_item} ${selected === i ? styles.selectedCSS : ""}`}
              >
                {adminVendorId === productVendorId && (
                  <DragHandle
                    {...dragHandleProps}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {type.name}
                {adminVendorId === productVendorId && (
                  <div className={styles.interValOperaions}>
                    <BiSolidEdit
                      onClick={() => handleEditType(type, i)}
                      className={(styles.icon, styles.edit_icon)}
                    />
                    <RxCross2
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteType(i);
                      }}
                      className={(styles.icon, styles.delete_icon)}
                    />
                  </div>
                )}
              </li>
            )}
          />
          {adminVendorId === productVendorId && (
            <div className={styles.topplanIntervals}>
              <button onClick={() => setshowPlanType(true)}>Add</button>
            </div>
          )}
        </ul>
        {showPlanType && (
          <>
            <DynamicPlanInterval
              heading={
                Object.keys(typeInput).length
                  ? "Edit Plan Type"
                  : "Add Plan Type"
              }
              handleClose={() => {
                setEditingPlanIndex(null);
                setTypeInput({});
                setshowPlanType(false);
              }}
              placeholder="Add plan type name"
              type="plan"
              typeId={null}
              value={typeInput}
              isOneTime={false}
              setStateVal={setTypeInput}
              handleSavePlan_interval={handleSaveType}
              adminVendorId={adminVendorId}
            />
          </>
        )}
      </div>

      {/* Required fields */}
      <div className={styles.formGroup} style={{ marginTop: 18 }}>
        <label className={styles.RequiredTitle}>
          Required Fields
          <FieldInfo
            text={FIELD_INFO["type.required_fields"]}
            label="About required fields"
          />
        </label>
        <Button
          className={styles.addFieldButton}
          onClick={requiredFieldOpenHandler}
        >
          <FaPlus />
        </Button>
        {showRequiredFieldModal && (
          <MarketPlaceRequiredField
            jsonData={planType[selected].required_fields}
            setJsonData={(fields) => {
              const temp = [...planType];
              temp[selected] = {
                ...temp[selected],
                required_fields: fields,
              };
              setPlanType(temp);
            }}
            requiredFieldCloseHandler={requiredFieldCloseHandler}
            adminVendorId={adminVendorId}
            productVendorId={productVendorId}
          />
        )}
      </div>
    </div>
  );

  /* ---------- Step 2 — INTERVAL ---------- */
  const step2Content = (
    <div className={styles.stepSection}>
      <div className={styles.intervalContainer}>
        <div className={styles.intervalheading}>
          Billing interval
          <FieldInfo text={FIELD_INFO["interval"]} label="About interval" />
        </div>
        <ul className={styles.interval_list}>
          {(() => {
            const subset = intervals
              .map((iv, idx) => ({ ...iv, originalIndex: idx }))
              .filter((iv) => iv.typeId === selected);

            return (
              <SortableList
                items={subset}
                strategy="horizontal"
                disabled={adminVendorId !== productVendorId}
                getId={(iv) =>
                  iv.id != null
                    ? `iv-${iv.id}`
                    : `iv-orig-${iv.originalIndex}`
                }
                onReorder={(nextSubset) => {
                  const slotIndices = subset.map((s) => s.originalIndex);
                  const next = [...intervals];
                  const ivIdxMap = new Map();
                  nextSubset.forEach((item, i) => {
                    const slot = slotIndices[i];
                    const { originalIndex: oldIdx, ...clean } = item;
                    next[slot] = { ...clean, index: slot };
                    if (oldIdx !== slot) ivIdxMap.set(oldIdx, slot);
                  });

                  let newSelectIdx = selectInterval;
                  if (ivIdxMap.has(selectInterval)) {
                    newSelectIdx = ivIdxMap.get(selectInterval);
                  }

                  setIntervals(next);
                  setSelectedInterval(newSelectIdx);
                  if (ivIdxMap.size) {
                    setPlans((prev) => {
                      const remapped = prev.map((p) => {
                        const r = ivIdxMap.get(Number(p.interval));
                        return r === undefined
                          ? p
                          : { ...p, interval: r };
                      });

                      const slots = [];
                      const typePlans = [];
                      remapped.forEach((p, i) => {
                        if (Number(p.typeId) === selected) {
                          slots.push(i);
                          typePlans.push(p);
                        }
                      });
                      typePlans.sort(
                        (a, b) =>
                          Number(a.interval) - Number(b.interval),
                      );
                      const final = [...remapped];
                      slots.forEach((slot, i) => {
                        final[slot] = typePlans[i];
                      });
                      return final;
                    });
                  }
                }}
                renderItem={(
                  interval,
                  _i,
                  { sortableProps, dragHandleProps },
                ) => (
                  <li
                    ref={sortableProps.ref}
                    style={sortableProps.style}
                    key={
                      interval.id != null
                        ? `iv-${interval.id}`
                        : `iv-orig-${interval.originalIndex}`
                    }
                    className={`${styles.plan_item} ${
                      selectInterval === interval.originalIndex
                        ? styles.selectedCSS
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedInterval(interval.originalIndex)
                    }
                  >
                    {adminVendorId === productVendorId && (
                      <DragHandle
                        {...dragHandleProps}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    {interval.n_interval} {interval.interval}
                    {adminVendorId === productVendorId && (
                      <div className={styles.interValOperaions}>
                        <BiSolidEdit
                          onClick={() =>
                            handleEditInterval(
                              interval,
                              interval.originalIndex,
                            )
                          }
                        />
                        <RxCross2
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteInterval(interval.originalIndex);
                          }}
                        />
                      </div>
                    )}
                  </li>
                )}
              />
            );
          })()}
          {adminVendorId === productVendorId && (
            <button onClick={() => setShowInterval(true)}>Add</button>
          )}
        </ul>
        {showInterval && (
          <DynamicPlanInterval
            heading={
              Object.keys(interval).length
                ? "Edit Interval"
                : "Add Interval"
            }
            placeholder="Choose interval"
            handleClose={() => {
              setInterval({});
              setShowInterval(false);
            }}
            type="interval"
            typeId={selected}
            value={interval}
            isOneTime={planType[selected].template === "one_time"}
            setStateVal={setInterval}
            handleSavePlan_interval={handleSaveInterval}
            adminVendorId={adminVendorId}
          />
        )}
      </div>
    </div>
  );

  /* ---------- Step 3 — PRICE editor + live preview ---------- */
  const priceEditor = (
    <div className={styles.priceEditorWrap}>
      {planType[selected]?.template === "recurring" && (
        <MarketPlaceT1
          selected={selected}
          planType={planType}
          selectInterval={selectInterval}
          intervals={filteredIntervalData}
          originalInterval={intervals}
          planErrors={planErrors}
          setPlanErrors={setPlanErrors}
          featureErrors={featureErrors}
          setFeatureErrors={setFeatureErrors}
          plans={filteredAppsData}
          originalPlan={plans}
          setPlans={setPlans}
          features={filteredFeaturesData}
          setFeatures={setFeatures}
          adminVendorId={adminVendorId}
          productVendorId={productVendorId}
        />
      )}
      {planType[selected]?.template === "credit" && (
        <MarketPlaceT2
          selected={selected}
          planType={planType}
          selectInterval={selectInterval}
          intervals={filteredIntervalData}
          originalInterval={intervals}
          planErrors={planErrors}
          setPlanErrors={setPlanErrors}
          featureErrors={featureErrors}
          setFeatureErrors={setFeatureErrors}
          plans={filteredAppsData}
          originalPlan={plans}
          setPlans={setPlans}
          features={filteredFeaturesData}
          setFeatures={setFeatures}
          adminVendorId={adminVendorId}
          productVendorId={productVendorId}
        />
      )}
      {planType[selected]?.template === "one_time" && (
        <MarketPlaceT3
          selected={selected}
          planType={planType}
          selectInterval={selectInterval}
          intervals={filteredIntervalData}
          originalInterval={intervals}
          planErrors={planErrors}
          setPlanErrors={setPlanErrors}
          featureErrors={featureErrors}
          setFeatureErrors={setFeatureErrors}
          plans={filteredAppsData}
          originalPlan={plans}
          setPlans={setPlans}
          features={filteredFeaturesData}
          setFeatures={setFeatures}
          adminVendorId={adminVendorId}
          productVendorId={productVendorId}
        />
      )}
    </div>
  );

  const previewColumn = (
    <div className={styles.previewColumn}>
      {filteredAppsData.length === 0 ? (
        <MarketplacePreview plan={null} />
      ) : (
        renderSortablePlanCards()
      )}
      <FieldGuide />
    </div>
  );

  const step3Content = (
    <div className={styles.priceStepGrid}>
      <div className={styles.priceStepEditor}>{priceEditor}</div>
      <div className={styles.priceStepPreview}>{previewColumn}</div>
    </div>
  );

  return (
    <div className={styles.Container}>
      {/* Who governs the plan NAMES on this product, and the one button that
          changes it. Ownership decides who may rename; it never moves a
          seller, a price or a subscription. */}
      <ProductOwnership
        product={selectedMarketPlace}
        onChanged={(next) => setSelectedMarketPlace(next)}
      />
      <div className={styles.topContainer}>
        <div className={styles.back_filter}>
          <div className={styles.backButton}>
            <BsArrowLeftCircle
              style={{ fontSize: "24px", borderRadius: "50%" }}
              onClick={() => setSelectedMarketPlace({})}
            />
          </div>
          <div className={styles.topRightContainer}>
            <div className={styles.filterBox}>
              <Select
                value={activeStatus}
                onChange={(e) => setActiveStatus(e.target.value)}
                displayEmpty
                sx={{ width: "30%" }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
                <MenuItem value="Out Of Stock">Out of Stock</MenuItem>
              </Select>
              <Select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                displayEmpty
                sx={{ width: "30%" }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GL">GLOBAL</MenuItem>
                <MenuItem value="US">US</MenuItem>
                <MenuItem value="IN">IN</MenuItem>
              </Select>
            </div>
            {isOwner && (
              <button
                type="button"
                onClick={() => openEditor(1)}
                style={{
                  whiteSpace: "nowrap",
                  border: "1px solid #4f46e5",
                  background: "#4f46e5",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Edit listing
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Overview: browse type/interval + live marketplace preview */}
      <div>
        {fetchLoader && (
          <div className={styles.fetchLoader}>
            <CircularProgress size={25} />
          </div>
        )}
        {!fetchLoader && (
          <div className={styles.overviewContainer}>
            {productVendorId !== adminVendorId && (
              <p style={{ color: "red" }}>
                **You can only add your unit prices as this product is owned by
                some other vendor. If you have any request you can put comments
                in the Review Chat section after first approval reuqest. Admins
                will do the needful and get back to you.
              </p>
            )}

            <div className={styles.overviewPickers}>
              <div className={styles.overviewPickerGroup}>
                <span className={styles.overviewPickerLabel}>Type</span>
                <div className={styles.overviewChips}>
                  {planType.map((t, i) => (
                    <button
                      key={t.id != null ? `ovt-${t.id}` : `ovt-${i}`}
                      type="button"
                      className={`${styles.overviewChip} ${
                        selected === i ? styles.overviewChipActive : ""
                      }`}
                      onClick={() => setSelected(i)}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.overviewPickerGroup}>
                <span className={styles.overviewPickerLabel}>Interval</span>
                <div className={styles.overviewChips}>
                  {filteredIntervalData.length === 0 && (
                    <span className={styles.overviewEmpty}>
                      No intervals yet
                    </span>
                  )}
                  {filteredIntervalData.map((iv) => (
                    <button
                      key={iv.id != null ? `ovi-${iv.id}` : `ovi-${iv.index}`}
                      type="button"
                      className={`${styles.overviewChip} ${
                        selectInterval === iv.index
                          ? styles.overviewChipActive
                          : ""
                      }`}
                      onClick={() => setSelectedInterval(iv.index)}
                    >
                      {iv.interval === "lifetime"
                        ? "No Expiry"
                        : `Every ${iv.n_interval} ${iv.interval}`}
                    </button>
                  ))}
                </div>
              </div>
              {isOwner && (
                <button
                  type="button"
                  className={styles.overviewEditBtn}
                  onClick={() => openEditor(3)}
                >
                  {filteredAppsData.length ? "Edit pricing" : "Add price"}
                </button>
              )}
            </div>

            <div className={styles.overviewPreviewGrid}>
              {filteredAppsData.length === 0 ? (
                <MarketplacePreview plan={null} />
              ) : (
                renderSortablePlanCards()
              )}
            </div>
          </div>
        )}
      </div>

      <ListingEditorSidebar
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        step={step}
        setStep={setStep}
        steps={steps}
        stepUnlocked={stepUnlocked}
        canNext={canNext}
        onSave={handleSubmit}
        saveLabel="Save"
        saving={submitLoader}
        step1={step1Content}
        step2={step2Content}
        step3={step3Content}
      />

      {/* Phase 4 — what a published price version is still waiting to do.
          Renders nothing when there is nothing scheduled (or before the
          phase-4 migration). */}
      <ScheduledPriceChanges skuId={selectedMarketPlace.id} />

      {submitconfirmation && (
        <SubmitConfirmation
          handleClose={() => setSubmitConfirmation(false)}
          // The rollout choice is INSIDE this dialog when the save moves a
          // price on a plan with live subscriptions, so the warning and the
          // choice are read together and there is no second dialog to miss.
          handleSave={(rollout) => handleSave(rollout)}
          submitLoader={submitLoader}
          warnActiveSubs={activeSubPriceChange}
          priceIds={(plans || []).map((p) => p.id).filter(Boolean)}
        />
      )}
    </div>
  );
}

export default MarketPlaceDetails;
